import type { FunctionComponent } from 'react';
import { useContext } from 'react';
import type { Control } from 'react-hook-form';

import { ApplicationContext } from '~/shared/context/context';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconAsterisk, IconPlus } from '~/ui/core/icons';

import { CompetencySearchContent } from './skills/CompetenciesSuggestions';
import { SkillSearchContent } from './skills/SkillSearch';
import type { ProfileForm } from './types';

type EditSkillsAndCompetenciesOptionsProps = {
    readonly architectureId?: string;
    readonly control: Control<ProfileForm>;
    readonly currentSkillCount: number;
    readonly hasUnsavedChanges: boolean;
    readonly isSkillLimitReached: boolean;
    readonly itemType: 'job-level' | 'job-role';
    readonly maximumSkillLimit: number | undefined;
    readonly onCompetencyChange: () => void;
    readonly onRegenerate: () => void;
    readonly onSkillChange: () => void;
};

export const EditSkillsAndCompetenciesOptions: FunctionComponent<EditSkillsAndCompetenciesOptionsProps> = ({
    architectureId,
    control,
    currentSkillCount,
    hasUnsavedChanges,
    isSkillLimitReached,
    itemType,
    maximumSkillLimit,
    onCompetencyChange,
    onRegenerate,
    onSkillChange,
}) => {
    const { translate } = useContext(ApplicationContext);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger>
                <Button iconPosition="leading" size="sm" variant="secondary">
                    <IconPlus />
                    {translate('common.edit')}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={5}>
                {isSkillLimitReached ? (
                    <DropdownMenuItem
                        disabled={true}
                        tooltipContent={translate(
                            itemType === 'job-role'
                                ? 'pages.job-architecture-v2.skill-limit-reached-role'
                                : 'pages.job-architecture-v2.skill-limit-reached-level',
                            {
                                current: currentSkillCount,
                                maximum: maximumSkillLimit,
                            },
                        )}
                    >
                        {translate('common.add')} {translate('common.skill')}
                    </DropdownMenuItem>
                ) : (
                    <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                            {translate('common.add')} {translate('common.skill')}
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                            <SkillSearchContent
                                architectureId={architectureId}
                                control={control}
                                onSkillChange={onSkillChange}
                            />
                        </DropdownMenuSubContent>
                    </DropdownMenuSub>
                )}
                <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                        {translate('common.add')} {translate('pages.job-architecture-v2.add-competency-button')}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                        <CompetencySearchContent
                            architectureId={architectureId}
                            control={control}
                            onCompetencyChange={onCompetencyChange}
                        />
                    </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuItem disabled={hasUnsavedChanges} onClick={onRegenerate}>
                    <IconAsterisk style={{ height: '16px', width: '16px' }} />
                    {translate('pages.job-architecture-v2.job-profile-regenerate')}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
